import { ImagePlus, Loader2, X } from 'lucide-react';
import { type ChangeEvent, type FormEvent, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  type BlogCategory,
  type BlogChannel,
  type BlogPost,
  type BlogPostTranslation,
  type CreateBlogPostPayload,
  useUploadBlogImage,
} from '../../hooks/useBlogData';
import { slugify } from '../../lib/slug';

interface BlogPostFormProps {
  initial?: BlogPost | null;
  channels: BlogChannel[];
  categories: BlogCategory[];
  onSaveDraft: (payload: CreateBlogPostPayload) => void;
  onSavePublish: (payload: CreateBlogPostPayload) => void;
  isPending: boolean;
  onClose: () => void;
}

type EditorLocale = 'en' | 'es' | 'pt';
type TranslatableLocale = 'es' | 'pt';

interface LocaleFields {
  title: string;
  excerpt: string;
  body: string;
}

const LOCALE_TABS: { key: EditorLocale; label: string }[] = [
  { key: 'en', label: 'EN' },
  { key: 'es', label: 'ES' },
  { key: 'pt', label: 'PT' },
];

const TRANSLATABLE_LOCALES: TranslatableLocale[] = ['es', 'pt'];

export function BlogPostForm({
  initial,
  channels,
  categories,
  onSaveDraft,
  onSavePublish,
  isPending,
  onClose,
}: BlogPostFormProps) {
  const [activeLocale, setActiveLocale] = useState<EditorLocale>('en');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [slugDraft, setSlugDraft] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(initial?.slug));
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [coverImageUrl, setCoverImageUrl] = useState(initial?.coverImageUrl ?? '');
  const [translations, setTranslations] = useState<Record<TranslatableLocale, LocaleFields>>(() => {
    const load = (locale: TranslatableLocale): LocaleFields => {
      const existing = initial?.translations?.find((t) => t.locale === locale);
      return {
        title: existing?.title ?? '',
        excerpt: existing?.excerpt ?? '',
        body: existing?.body ?? '',
      };
    };
    return { es: load('es'), pt: load('pt') };
  });
  const [selectedChannelKeys, setSelectedChannelKeys] = useState<string[]>(
    (initial?.channels ?? []).map((c) => c.key),
  );
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<string[]>(
    (initial?.categories ?? []).map((c) => c.key),
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadImage = useUploadBlogImage();

  const slug = slugTouched ? slugDraft : slugify(title);

  const isValid = useMemo(
    () => title.trim().length > 0 && body.trim().length > 0 && selectedChannelKeys.length > 0,
    [title, body, selectedChannelKeys],
  );

  const activeFields: LocaleFields =
    activeLocale === 'en' ? { title, excerpt, body } : translations[activeLocale];

  function setLocaleField(field: keyof LocaleFields, value: string) {
    if (activeLocale === 'en') {
      if (field === 'title') setTitle(value);
      else if (field === 'excerpt') setExcerpt(value);
      else setBody(value);
      return;
    }
    const locale = activeLocale;
    setTranslations((prev) => ({
      ...prev,
      [locale]: { ...prev[locale], [field]: value },
    }));
  }

  function localeHasContent(locale: TranslatableLocale): boolean {
    const f = translations[locale];
    return f.title.trim().length > 0 || f.body.trim().length > 0;
  }

  function toggleChannel(key: string) {
    setSelectedChannelKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function toggleCategory(key: string) {
    setSelectedCategoryKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadImage.mutate(file, {
      onSuccess: (data) => setCoverImageUrl(data.url),
    });
    e.target.value = '';
  }

  function buildTranslations(): BlogPostTranslation[] | undefined {
    const result: BlogPostTranslation[] = [];
    for (const locale of TRANSLATABLE_LOCALES) {
      const f = translations[locale];
      // A translation is only sent when BOTH title and body are provided.
      // Partial locales (only one of the two) are skipped.
      if (f.title.trim().length > 0 && f.body.trim().length > 0) {
        result.push({
          locale,
          title: f.title.trim(),
          excerpt: f.excerpt.trim() ? f.excerpt.trim() : undefined,
          body: f.body,
        });
      }
    }
    return result.length > 0 ? result : undefined;
  }

  function buildPayload(): CreateBlogPostPayload {
    return {
      title: title.trim(),
      slug: slug || undefined,
      excerpt: excerpt.trim() ? excerpt.trim() : undefined,
      body,
      coverImageUrl: coverImageUrl || undefined,
      channelKeys: selectedChannelKeys,
      categoryKeys: selectedCategoryKeys,
      translations: buildTranslations(),
    };
  }

  function handleDraft(e: FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    onSaveDraft(buildPayload());
  }

  function handlePublish(e: FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    onSavePublish(buildPayload());
  }

  const inputClass =
    'w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors';

  const isBase = activeLocale === 'en';

  return (
    <form className="space-y-4">
      <div>
        <div className="flex items-center gap-1 border-b border-border" role="tablist" aria-label="Content language">
          {LOCALE_TABS.map((tab) => {
            const active = activeLocale === tab.key;
            const filled = tab.key !== 'en' && localeHasContent(tab.key);
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveLocale(tab.key)}
                className={`-mb-px flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text'
                }`}
              >
                {tab.label}
                {tab.key === 'en' && (
                  <span className="text-[10px] font-normal text-text-muted">(base)</span>
                )}
                {filled && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        {!isBase && (
          <p className="mt-2 text-xs text-text-muted">
            Optional translation. Provide both title and body to save this locale; leave empty to
            skip it.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Title {isBase && <span className="text-danger">*</span>}
        </label>
        <input
          type="text"
          value={activeFields.title}
          onChange={(e) => setLocaleField('title', e.target.value)}
          required={isBase}
          aria-label="Title"
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Excerpt</label>
        <textarea
          value={activeFields.excerpt}
          onChange={(e) => setLocaleField('excerpt', e.target.value)}
          rows={2}
          placeholder="Short summary shown in listings (optional)"
          className={`${inputClass} resize-y`}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Body (Markdown) {isBase && <span className="text-danger">*</span>}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <textarea
            value={activeFields.body}
            onChange={(e) => setLocaleField('body', e.target.value)}
            rows={14}
            placeholder="Write your post in Markdown..."
            aria-label="Body (Markdown)"
            className={`${inputClass} resize-y font-mono`}
          />
          <div className="border border-border rounded-lg bg-surface px-3 py-2 overflow-y-auto max-h-[22rem]">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-2">
              Preview
            </p>
            {activeFields.body.trim() ? (
              <div className="blog-markdown-preview prose-sm text-sm text-text space-y-2">
                <ReactMarkdown>{activeFields.body}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-text-muted">Nothing to preview yet.</p>
            )}
          </div>
        </div>
      </div>

      <div className="pt-2 border-t border-border space-y-4">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Shared across all languages
        </p>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlugDraft(e.target.value);
              setSlugTouched(true);
            }}
            placeholder="auto-generated-from-title"
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Cover image</label>
          <div className="flex items-center gap-3">
            {coverImageUrl ? (
              <div className="relative">
                <img
                  src={coverImageUrl}
                  alt="Cover preview"
                  className="h-16 w-24 object-cover rounded-lg border border-border"
                />
                <button
                  type="button"
                  onClick={() => setCoverImageUrl('')}
                  aria-label="Remove cover image"
                  className="absolute -top-2 -right-2 p-0.5 rounded-full bg-surface border border-border text-text-muted hover:text-danger transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div className="h-16 w-24 rounded-lg border border-dashed border-border flex items-center justify-center text-text-muted">
                <ImagePlus size={18} />
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              data-testid="cover-image-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadImage.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadImage.isPending ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <ImagePlus size={15} /> {coverImageUrl ? 'Replace image' : 'Upload image'}
                </>
              )}
            </button>
          </div>
          {uploadImage.isError && (
            <p className="mt-1 text-xs text-danger">Failed to upload image. Try again.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Channels <span className="text-danger">*</span>
          </label>
          {channels.length === 0 ? (
            <p className="text-sm text-text-muted">No channels available. Create one first.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {channels.map((channel) => {
                const active = selectedChannelKeys.includes(channel.key);
                return (
                  <button
                    key={channel.id}
                    type="button"
                    onClick={() => toggleChannel(channel.key)}
                    aria-pressed={active}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      active
                        ? 'bg-primary-muted border-primary text-primary'
                        : 'border-border text-text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    {channel.name}
                  </button>
                );
              })}
            </div>
          )}
          {selectedChannelKeys.length === 0 && (
            <p className="mt-1 text-xs text-text-muted">Select at least one channel.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Categories</label>
          {categories.length === 0 ? (
            <p className="text-sm text-text-muted">No categories available.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const active = selectedCategoryKeys.includes(category.key);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => toggleCategory(category.key)}
                    aria-pressed={active}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      active
                        ? 'bg-primary-muted border-primary text-primary'
                        : 'border-border text-text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDraft}
          disabled={isPending || !isValid}
          className="px-4 py-2 text-sm rounded-lg border border-border font-medium text-text-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? 'Saving...' : 'Save as draft'}
        </button>
        <button
          type="button"
          onClick={handlePublish}
          disabled={isPending || !isValid}
          className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? 'Saving...' : 'Save & publish'}
        </button>
      </div>
    </form>
  );
}
